<template>
  <div class="cross-event-drawer" :class="{ 'cross-event-drawer--open': foundRun != null }">
    <template v-if="foundRun">
      <!-- Confirmation -->
      <div v-if="moved" class="cross-event-drawer__done">
        <span class="cross-event-drawer__check"><i class="pi pi-check" /></span>
        <div class="cross-event-drawer__done-text">
          <span class="cross-event-drawer__done-title">Run #{{ foundRun.run }} moved into {{ targetLane }}</span>
          <span class="cross-event-drawer__done-subtitle">Added to the top of this event's run list.</span>
        </div>
      </div>

      <!-- Prompt -->
      <template v-else>
        <div class="cross-event-drawer__head">
          <div class="cross-event-drawer__head-left">
            <span class="cross-event-drawer__head-icon"><i class="pi pi-arrow-right-arrow-left" /></span>
            <span class="cross-event-drawer__head-label">Found in another event</span>
          </div>
          <Button class="cross-event-drawer__dismiss" icon="pi pi-times" text title="Dismiss" @click="emit('close')" />
        </div>

        <div class="cross-event-drawer__summary">
          <span class="cross-event-drawer__summary-run">#{{ foundRun.run }}</span>
          <span class="cross-event-drawer__summary-title">
            <span class="cross-event-drawer__summary-year">{{ foundRun.year }}</span>{{ foundRun.title }}
          </span>
          <span class="cross-event-drawer__summary-meta">
            <span class="cross-event-drawer__summary-event">
              <i class="pi pi-calendar cross-event-drawer__summary-event-icon" />
              {{ foundRun.event }} · {{ foundRun.lane }}
            </span>
            <span class="cross-event-drawer__summary-separator">·</span>
            <span class="cross-event-drawer__summary-miles">{{ (foundRun.miles || 0).toLocaleString() }} mi</span>
          </span>
        </div>

        <p class="cross-event-drawer__prompt">
          This run belongs to <strong>{{ foundRun.event }}</strong>. Move it into
          <strong>{{ targetEventName }} · {{ targetLane }}</strong>?
        </p>

        <div class="cross-event-drawer__actions">
          <Button class="cross-event-drawer__cancel" label="Cancel" @click="emit('close')" />
          <Button class="cross-event-drawer__move" @click="emit('move')">
            <i class="pi pi-arrow-down" />
            Move to this event
          </Button>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import Button from 'primevue/button';

// The sheet that rises inside the run list when the clerk searches a run number
// that belongs to a DIFFERENT event, asking whether to pull it into this one.
//
// Its whole reason to exist is that the search box cannot silently fail: a
// clerk typing a run they can see on a printed sheet needs to be told the lot
// is real but somewhere else, not shown "no results".
//
// It slides rather than appearing, and the shadow is tied to the open state —
// closed, it sits translated below the panel, and an upward shadow on a
// closed sheet bleeds a dark band across the bottom of the run list.
defineOptions({ name: 'CrossEventDrawer' });

defineProps({
  // { run, year, title, event, lane, miles } — the lot found elsewhere, or null
  // to keep the sheet down.
  foundRun: { type: Object, default: null },
  // The move has been accepted; show the confirmation instead of the prompt.
  moved: { type: Boolean, default: false },
  // Where the lot would move TO — this console's own sale.
  targetEventName: { type: String, default: '' },
  targetLane: { type: String, default: '' },
});

const emit = defineEmits(['move', 'close']);
</script>

<style scoped>
.cross-event-drawer {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  transform: translateY(105%);
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
  background: var(--p-cross-event-drawer-background);
  border-top: var(--p-cross-event-drawer-border-top-width) solid var(--p-cross-event-drawer-border-top-color);
  border-radius: var(--p-cross-event-drawer-border-radius);
  padding: var(--p-cross-event-drawer-padding);
  gap: var(--p-cross-event-drawer-gap);
}

.cross-event-drawer--open {
  transform: translateY(0);
  box-shadow: var(--p-cross-event-drawer-box-shadow);
}

/* ── Confirmation ───────────────────────────────────────────────────────── */

.cross-event-drawer__done {
  display: flex;
  align-items: center;
  padding: 4px 2px;
  gap: var(--p-cross-event-drawer-done-gap);
}

.cross-event-drawer__check {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-cross-event-drawer-check-border-radius);
  background: var(--p-cross-event-drawer-check-background);
  color: var(--p-cross-event-drawer-check-color);
  font-size: var(--p-cross-event-drawer-check-font-size);
}

.cross-event-drawer__done-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.cross-event-drawer__done-title {
  font-size: var(--p-cross-event-drawer-done-title-font-size);
  font-weight: var(--p-cross-event-drawer-done-title-font-weight);
  color: var(--p-cross-event-drawer-done-title-color);
}

.cross-event-drawer__done-subtitle {
  font-size: var(--p-cross-event-drawer-done-subtitle-font-size);
  color: var(--p-cross-event-drawer-done-subtitle-color);
}

/* ── Prompt ─────────────────────────────────────────────────────────────── */

.cross-event-drawer__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--p-cross-event-drawer-head-gap);
}

.cross-event-drawer__head-left {
  display: flex;
  align-items: center;
  gap: var(--p-cross-event-drawer-head-left-gap);
}

.cross-event-drawer__head-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-cross-event-drawer-head-icon-border-radius);
  background: var(--p-cross-event-drawer-head-icon-background);
  color: var(--p-cross-event-drawer-head-icon-color);
  font-size: var(--p-cross-event-drawer-head-icon-font-size);
}

.cross-event-drawer__head-label {
  text-transform: uppercase;
  font-size: var(--p-cross-event-drawer-head-label-font-size);
  font-weight: var(--p-cross-event-drawer-head-label-font-weight);
  letter-spacing: var(--p-cross-event-drawer-head-label-letter-spacing);
  color: var(--p-cross-event-drawer-head-label-color);
}

.p-button.cross-event-drawer__dismiss {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  border-radius: var(--p-cross-event-drawer-dismiss-border-radius);
  color: var(--p-cross-event-drawer-dismiss-color);
}

.cross-event-drawer__summary {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: var(--p-cross-event-drawer-summary-gap);
  padding: var(--p-cross-event-drawer-summary-padding);
  border-radius: var(--p-cross-event-drawer-summary-border-radius);
  background: var(--p-cross-event-drawer-summary-background);
  border: var(--p-cross-event-drawer-summary-border-width) solid var(--p-cross-event-drawer-summary-border-color);
}

.cross-event-drawer__summary-run {
  grid-row: 1 / 3;
  align-self: center;
  font-family: var(--p-cross-event-drawer-summary-run-font-family);
  font-weight: var(--p-cross-event-drawer-summary-run-font-weight);
  font-size: var(--p-cross-event-drawer-summary-run-font-size);
  color: var(--p-cross-event-drawer-summary-run-color);
  letter-spacing: var(--p-cross-event-drawer-summary-run-letter-spacing);
  padding: var(--p-cross-event-drawer-summary-run-padding);
  border-radius: var(--p-cross-event-drawer-summary-run-border-radius);
  background: var(--p-cross-event-drawer-summary-run-background);
}

.cross-event-drawer__summary-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-cross-event-drawer-summary-title-font-size);
  font-weight: var(--p-cross-event-drawer-summary-title-font-weight);
  color: var(--p-cross-event-drawer-summary-title-color);
}

.cross-event-drawer__summary-year {
  margin-right: 6px;
  font-weight: var(--p-cross-event-drawer-summary-year-font-weight);
  color: var(--p-cross-event-drawer-summary-year-color);
}

.cross-event-drawer__summary-meta {
  display: inline-flex;
  align-items: center;
  font-size: var(--p-cross-event-drawer-summary-meta-font-size);
  color: var(--p-cross-event-drawer-summary-meta-color);
  gap: var(--p-cross-event-drawer-summary-meta-gap);
}

.cross-event-drawer__summary-event {
  display: inline-flex;
  align-items: center;
  gap: var(--p-cross-event-drawer-summary-event-gap);
}

.cross-event-drawer__summary-event-icon {
  font-size: var(--p-cross-event-drawer-summary-event-icon-font-size);
  color: var(--p-cross-event-drawer-summary-event-icon-color);
}

.cross-event-drawer__summary-separator {
  color: var(--p-cross-event-drawer-summary-separator-color);
}

.cross-event-drawer__summary-miles {
  font-family: var(--p-cross-event-drawer-summary-miles-font-family);
}

.cross-event-drawer__prompt {
  margin: 0;
  font-size: var(--p-cross-event-drawer-prompt-font-size);
  color: var(--p-cross-event-drawer-prompt-color);
  line-height: var(--p-cross-event-drawer-prompt-line-height);
}

.cross-event-drawer__prompt strong {
  color: var(--p-cross-event-drawer-prompt-strong-color);
}

.cross-event-drawer__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--p-cross-event-drawer-actions-gap);
}

.p-button.cross-event-drawer__cancel {
  font-family: inherit;
  border: var(--p-cross-event-drawer-cancel-border-width) solid var(--p-cross-event-drawer-cancel-border-color);
  background: var(--p-cross-event-drawer-cancel-background);
  color: var(--p-cross-event-drawer-cancel-color);
  border-radius: var(--p-cross-event-drawer-cancel-border-radius);
  padding: var(--p-cross-event-drawer-cancel-padding);
  font-size: var(--p-cross-event-drawer-cancel-font-size);
  font-weight: var(--p-cross-event-drawer-cancel-font-weight);
}

.p-button.cross-event-drawer__move {
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  border: var(--p-cross-event-drawer-move-border-width) solid var(--p-cross-event-drawer-move-border-color);
  background: var(--p-cross-event-drawer-move-background);
  color: var(--p-cross-event-drawer-move-color);
  border-radius: var(--p-cross-event-drawer-move-border-radius);
  padding: var(--p-cross-event-drawer-move-padding);
  font-size: var(--p-cross-event-drawer-move-font-size);
  font-weight: var(--p-cross-event-drawer-move-font-weight);
  gap: var(--p-cross-event-drawer-move-gap);
}
</style>
